/**
 * Extension Registry
 *
 * Each extension is a lightweight, UI-only bundle of settings that adapts
 * what fields appear in the sidebar and what menu items appear in action
 * dropdowns for selected items.
 *
 * Extensions are resolved at render time against each primitive's metadata
 * (category referenceId / type) so the same board or flow can surface
 * different extensions depending on what kind of item is selected.
 */

// ---------------------------------------------------------------------------
// Built-in extension definitions
// ---------------------------------------------------------------------------

const FINANCE_FIELDS = [
  { key: "financialData.totalRevenue", title: "Total Revenue", type: "currency" },
  { key: "financialData.revenueGrowth", title: "Revenue Growth", type: "percent" },
  { key: "financialData.grossProfit", title: "Gross Profit", type: "currency" },
  { key: "financialData.netIncome", title: "Net Income", type: "currency" },
  { key: "financialData.ebitda", title: "EBITDA", type: "currency" },
  { key: "financialData.marketCap", title: "Market Cap", type: "currency" },
  { key: "financialData.employeeCount", title: "Employees", type: "number" },
]

const SBTI_FIELDS = [
  { key: "sbti.status", title: "SBTi Status", type: "string" },
  { key: "sbti.targetType", title: "Target Type", type: "string" },
  { key: "sbti.targetYear", title: "Target Year", type: "number" },
  { key: "sbti.baseYear", title: "Base Year", type: "number" },
  { key: "sbti.scope", title: "Scope", type: "string" },
]

const FINANCE_ACTIONS = [
  { key: "convert_financials", title: "Convert Financials", icon: "FAMoney", menu: true },
]

const SBTI_ACTIONS = [
  { key: "sbti_target_scraper", title: "Fetch SBTi Targets", icon: "FAClimate", menu: true },
]

// Categories that company/org entities commonly use
const COMPANY_CATEGORIES = [1, 2, 5, 6, 14, 18, 30, 31, 38, 39, 77, 99, 100, 101, 110, 118, 142, 160, 167]

const EXTENSIONS = {
  company_finances: {
    id: "company_finances",
    title: "Company Finances",
    description: "Show financial data fields (revenue, profit, EBITDA, market cap) and actions.",
    forCategories: COMPANY_CATEGORIES,
    forTypes: ["result", "entity", "company"],
    sidebarFields: FINANCE_FIELDS,
    actionItems: FINANCE_ACTIONS,
  },
  sbti_targets: {
    id: "sbti_targets",
    title: "SBTi Targets",
    description: "Show Science Based Targets initiative data and fetch actions.",
    forCategories: COMPANY_CATEGORIES,
    forTypes: ["result", "entity", "company"],
    sidebarFields: SBTI_FIELDS,
    actionItems: SBTI_ACTIONS,
  },
}

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

/** @type {Map<string, ExtensionDef>} */
let registry = null

function ensureRegistry() {
  if (!registry) {
    registry = new Map(Object.entries(EXTENSIONS))
  }
  return registry
}

/**
 * Register a new extension at runtime.
 * @param {ExtensionDef} ext
 */
export function registerExtension(ext) {
  if (!ext || !ext.id) {
    console.warn("[extensions] Cannot register extension without an id", ext)
    return false
  }
  ensureRegistry().set(ext.id, { ...ext })
  return true
}

/**
 * Remove an extension from the registry.
 * @param {string} id
 */
export function unregisterExtension(id) {
  return ensureRegistry().delete(id)
}

/**
 * Get all registered extensions.
 * @returns {ExtensionDef[]}
 */
export function getAllExtensions() {
  return Array.from(ensureRegistry().values())
}

/**
 * Look up a single extension by id.
 * @param {string} id
 * @returns {ExtensionDef | undefined}
 */
export function getExtension(id) {
  return ensureRegistry().get(id)
}

/**
 * ExtensionDef schema (for documentation):
 *
 * @typedef {Object} ExtensionDef
 * @property {string}   id              - Unique extension id (e.g. "company_finances").
 * @property {string}   title           - Human-readable name.
 * @property {string}   description     - What the extension provides.
 * @property {number[]} [forCategories] - Array of category referenceIds this extension
 *                                        applies to. If omitted, applies to all.
 * @property {string[]} [forTypes]      - Array of primitive types (e.g. "result",
 *                                        "entity") this extension applies to.
 * @property {FieldDef[]}  [sidebarFields] - Fields to inject into the sidebar.
 * @property {ActionDef[]} [actionItems]   - Actions to inject into dropdown menus.
 *
 * @typedef {Object} FieldDef
 * @property {string} key   - Dot-path into referenceParameters (e.g. "financialData.totalRevenue").
 * @property {string} title - Display label.
 * @property {string} [type]    - Render hint (currency, percent, number, string, url, date).
 * @property {number} [order]   - Display order (defaults to 99).
 * @property {boolean} [hidden] - If true, never show.
 *
 * @typedef {Object} ActionDef
 * @property {string} key    - Action key registered via registerAction.
 * @property {string} title  - Menu item label.
 * @property {string} [icon] - Icon name.
 * @property {boolean} [menu] - Whether to show in the item's card menu.
 * @property {boolean} [collectionAction] - Show in collection-level actions.
 * @property {Array} [actionFields] - Fields to prompt for when executing.
 */
